// Clerk auth wiring for the React UI.
//
// Auth contract (issue #464):
//
// Clerk's `useAuth().getToken()` is only callable from React components.
// Most of our network code lives in plain modules (`game/api.ts`,
// `game/GameContext.tsx` SSE pump) that can't use hooks. To bridge the two
// worlds we let the in-tree `<TokenBridge>` component register a token
// getter at provider-mount time; non-React callers then read the token via
// the async `getAuthToken()` helper. The getter is null when Clerk isn't
// configured (dev-bypass build) — callers must treat a null/undefined
// token as "send the request without an Authorization header" so the
// backend's `LUCID_AUTH_DEV_BYPASS=1` path keeps working.
//
// Publishable key resolution mirrors `apiBase()` in game/api.ts:
//   1. window.LUCID_CLERK_PUBLISHABLE_KEY (runtime override via /config.js)
//   2. import.meta.env.VITE_CLERK_PUBLISHABLE_KEY (build-time, Dockerfile ARG)
//   3. undefined — render the app without <ClerkProvider> (dev-bypass).

export function getPublishableKey(): string | undefined {
  if (typeof window !== "undefined" && window.LUCID_CLERK_PUBLISHABLE_KEY) {
    return window.LUCID_CLERK_PUBLISHABLE_KEY;
  }
  const fromEnv = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  return fromEnv && fromEnv.length > 0 ? fromEnv : undefined;
}

export function hasClerk(): boolean {
  return getPublishableKey() !== undefined;
}

type TokenGetter = () => Promise<string | null>;

let _tokenGetter: TokenGetter | null = null;

export function registerTokenGetter(getter: TokenGetter | null): void {
  _tokenGetter = getter;
}

// Returns the current Clerk session token, or null when Clerk isn't
// configured / the user isn't signed in. Safe to call from non-React
// code; never throws.
export async function getAuthToken(): Promise<string | null> {
  if (!_tokenGetter) return null;
  try {
    return await _tokenGetter();
  } catch {
    return null;
  }
}
